import createFetchClient, { type Middleware } from "openapi-fetch";
import createClient from "openapi-react-query";
import { QueryClient } from "@tanstack/react-query";
import type { paths } from "./api-schema";
import { getEmbedAuth, type EmbedAuth } from "./auth";
import { setTrackingHeaders } from "./trackingHeaders";

// The one typed client every renderer talks to cortex through. The viewer
// never refreshes a credential itself: the only credentials are
// handshake-delivered (an auth token and/or a legacy API key), so a rejected
// one surfaces as an error state, and recovery is the host delivering a new
// credential over postMessage (see refetchOnCredentialChange below).

export class QueryError extends Error {
  statusCode: number;
  statusText: string;
  method: string;
  url: string;

  constructor(response: Response, method: string) {
    super(`${method} ${response.url} failed with status ${response.status}`);
    this.name = "QueryError";
    this.statusCode = response.status;
    this.statusText = response.statusText;
    this.method = method;
    this.url = response.url;
  }
}

/**
 * The shell's distinct failure modes — the viewer's *own* states, never an
 * interpretation of cortex's. `request-failed` covers every non-OK cortex
 * response and travels with the raw HTTP status (see ViewerError): cortex owns
 * what a 403 or 404 means, and that contract lives in one place — cortex —
 * rather than being re-encoded here and again in the embedding component,
 * where copies drift the moment cortex changes a code. `unsupported` and
 * `invalid-id` are never produced by `toViewerError`; renderers use them
 * directly. `file-unavailable` is for documents whose file lives outside
 * cortex (company docs, in Aiera's public content bucket): missing, outside
 * the bucket, or failing to load from it. It never carries a status — that
 * code would come from S3, not cortex, and must not be read as cortex's.
 */
export type ErrorKind =
  | "unknown-type"
  | "invalid-id"
  | "unsupported"
  | "file-unavailable"
  | "handshake-timeout"
  | "request-failed"
  | "network";

/**
 * A settled failure as the shell renders and reports it: the viewer's state
 * plus, for `request-failed`, cortex's raw HTTP status. The status is safe to
 * hand to the host: the host holds the credential and could make the same
 * request itself, so the number reveals nothing it couldn't already learn —
 * unlike response bodies, URLs, or status text, which never leave the frame.
 */
export interface ViewerError {
  kind: ErrorKind;
  status?: number;
}

/** Map a query failure onto the shell's error states. */
export function toViewerError(error: unknown): ViewerError {
  if (error instanceof QueryError) {
    return { kind: "request-failed", status: error.statusCode };
  }
  // No HTTP response at all — fetch TypeError (offline, DNS, CORS), abort.
  return { kind: "network" };
}

// Transient failures worth retrying on idempotent GETs.
const RETRY_STATUS_CODES = [408, 429, 500, 502, 503, 504];

/** Per-query flags read by `refetchOnCredentialChange`. */
export interface ViewerQueryMeta {
  /**
   * Leave this query alone when the credential changes, unless it holds no
   * result. For one-shot exchanges whose result has already been consumed
   * (the bank SSO handoffs): re-running them would reload the bank's page and
   * hit its IdP again for nothing, while the document-level queries upstream
   * already verify the new credential.
   */
  skipCredentialRefetch?: boolean;
}

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: ViewerQueryMeta;
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A document is immutable for the life of a load, so nothing goes stale
      // on its own: no refetch on tab focus, reconnect, or remount. With
      // short-lived tokens that matters — an automatic refetch under an
      // expired credential would swap a rendered document for an error. The
      // one refetch trigger is the host delivering a new credential
      // (refetchOnCredentialChange), and invalidation ignores staleTime.
      // staleTime alone isn't enough: a query with no data, or one whose last
      // refetch failed (which leaves it invalidated), counts as stale and
      // would still refetch on focus/reconnect under the same credential.
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false;
        if (error instanceof QueryError) {
          return RETRY_STATUS_CODES.includes(error.statusCode);
        }
        return true; // network-level failure
      },
    },
  },
});

/**
 * The one refetch trigger: the host delivered a new credential. Every settled
 * response was fetched under the old one, so the page's active queries refetch
 * — in the background where data exists (the document stays on screen and the
 * new credential is verified against it; a failure lands in the error state),
 * and as a recovery where the previous credential had failed. Queries flagged
 * `skipCredentialRefetch` only take the recovery path — keyed on "holds no
 * result" rather than status, because a data-less query is `pending` (not
 * `error`) while a retry is in flight, and a 200 with an empty payload is a
 * `success` the renderer still treats as a failure. An in-flight fetch is
 * cancelled and restarted under the new credential.
 */
export function refetchOnCredentialChange(): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      !query.meta?.skipCredentialRefetch || query.state.data == null,
  });
}

// In dev, requests go through the Vite proxy (vite.config.ts) so the browser
// sees them as same-origin; production builds call cortex directly
// (VITE_CORTEX_API_URL is validated at build time — see /env.ts). Tests need
// an absolute URL because undici's Request rejects relative ones.
const rawBaseUrl = import.meta.env.TEST
  ? "https://cortex.test"
  : import.meta.env.DEV
    ? ""
    : import.meta.env.VITE_CORTEX_API_URL;

// Trailing slash stripped so `${base}${path}` joins cleanly.
const baseUrl = rawBaseUrl.replace(/\/+$/, "");

/**
 * Base for cortex URLs constructed outside the typed client (e.g. the PDF
 * binary URL handed to pdf.js). "" in dev — relative URLs ride the proxy.
 * Append schema-validated absolute paths (`/api/v1/...`) by concatenation.
 */
export const CORTEX_BASE_URL = baseUrl;

/**
 * The full auth + tracking header set for a cortex request, as a plain
 * object. The typed client's middleware attaches exactly this; it's also
 * exported for requests that bypass the client entirely — pdf.js fetches the
 * PDF binary itself, so these must be passed to it explicitly or the request
 * goes out unauthenticated. React callers should pass the value from
 * `useEmbedAuth()` so they can rebuild their request when the host delivers a
 * new credential; the default reads the store once, at call time.
 */
export function authenticatedRequestHeaders(
  auth: EmbedAuth | null = getEmbedAuth(),
): Record<string, string> {
  const headers = new Headers();
  if (auth) {
    setCredentialHeader(headers, auth);
    // The validated embedding origin from the handshake (browser-asserted
    // MessageEvent.origin) — never derived from document.referrer.
    headers.set("X-Aiera-Parent-Origin", auth.parentOrigin);
    // The host component's self-reported version, when the handshake
    // delivered one (already normalized to a header-safe token).
    if (auth.hostVersion) {
      headers.set("X-Aiera-Host-Version", auth.hostVersion);
    }
  }
  setTrackingHeaders(headers);
  return Object.fromEntries(headers.entries());
}

// The auth token (bearer) is the go-forward credential; the API-key header is
// the legacy path and is only sent when no token arrived. Never both — cortex
// should see exactly one credential per request.
function setCredentialHeader(headers: Headers, auth: EmbedAuth): void {
  if (auth.authToken) {
    headers.set("Authorization", `Bearer ${auth.authToken}`);
  } else if (auth.apiKey) {
    headers.set("X-API-Key", auth.apiKey);
  }
}

export const fetchClient = createFetchClient<paths, "application/json">({
  baseUrl,
  // Resolve fetch at call time (openapi-fetch otherwise captures the global
  // at creation), so tests can stub globalThis.fetch after import.
  fetch: (request) => globalThis.fetch(request),
  headers: {
    "Content-Type": "application/json",
  },
});

const middleware: Middleware = {
  onRequest: ({ request }) => {
    for (const [name, value] of Object.entries(authenticatedRequestHeaders())) {
      request.headers.set(name, value);
    }
    return request;
  },
  onResponse: ({ response, request }) => {
    if (!response.ok) {
      throw new QueryError(response, request.method);
    }
    return response;
  },
};

fetchClient.use(middleware);

export const { useQuery, useSuspenseQuery, queryOptions } =
  createClient(fetchClient);
